import { createContext, useContext } from "react";
import { Table, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { EnumProductType } from "gql/generated";
import { SmartImage } from "shared/components";
import { getVariantImage } from "shared/merchCatalog";
import { formatPrice, getMultilingualString } from "shared/utils";
import { Flex, styled } from "styled-system/jsx";
import {
  useBookerContext,
  useCartContext,
  type CartItem,
} from "widgets/booker/contexts";
import { NumericStepper } from "widgets/booker/shared/NumericStepper";

const THUMBNAIL_SIZE = 44;

type CatalogCartEditing = {
  openItemDetails: (productId: string, variantId: string) => void;
  onCartEmptied: () => void;
};

const CatalogCartEditingContext = createContext<CatalogCartEditing | null>(
  null,
);

export const CatalogCartEditingProvider = CatalogCartEditingContext.Provider;

/** Merch cart keys are `${productId}::${variantId}`. */
const parseMerchCartKey = (item: CartItem) => {
  if (item.productType !== EnumProductType.Merch || !item.merchVariantId) {
    return null;
  }
  const [productId] = item.productId.split("::");
  return productId ? { productId, variantId: item.merchVariantId } : null;
};

/** Inside the catalog checkout, merch summary rows become editable; elsewhere this is a no-op. */
export const useCatalogCartItemRenderer = () => {
  const editing = useContext(CatalogCartEditingContext);
  if (!editing) return undefined;
  return (item: CartItem) =>
    parseMerchCartKey(item) ? <EditableMerchCartRow item={item} /> : undefined;
};

const LinkButton = styled("button", {
  base: {
    textAlign: "left",
    cursor: "pointer",
    bg: "transparent",
    border: "none",
    p: "0",
    color: "text.dark",
    _hover: { textDecoration: "underline" },
    _focusVisible: { outline: "2px solid", outlineColor: "border.dark" },
  },
});

const EditableMerchCartRow = ({ item }: { item: CartItem }) => {
  const editing = useContext(CatalogCartEditingContext);
  const { products, language } = useBookerContext();
  const { cart, updateCartItem, removeCartItem } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });

  const ids = parseMerchCartKey(item);
  const product = products.find(p => p._id === ids?.productId) ?? null;
  const variant = product?.merchMetadata?.variants?.find(
    v => v?._id === ids?.variantId,
  );
  const unitPrice =
    variant?.price ?? (item.quantity > 0 ? item.total / item.quantity : 0);
  const name = product
    ? getMultilingualString(product.name, language)
    : item.productName;
  const label = item.merchVariantLabel
    ? `${name} – ${item.merchVariantLabel}`
    : name;
  const image = getVariantImage(product, ids?.variantId);

  const openDetails = () => {
    if (ids) editing?.openItemDetails(ids.productId, ids.variantId);
  };

  const remove = () => {
    const isLastItem =
      cart.filter(i => i.quantity > 0 && i.productId !== item.productId)
        .length === 0;
    removeCartItem(item.productId);
    if (isLastItem) editing?.onCartEmptied();
  };

  const setQuantity = (quantity: number) => {
    if (quantity <= 0) {
      remove();
      return;
    }
    updateCartItem(item.productId, { quantity, total: unitPrice * quantity });
  };

  return (
    <Table.Row>
      <Table.Header paddingRight={"150"}>
        <Flex gap="150" alignItems="flex-start">
          {image && (
            <LinkButton
              type="button"
              onClick={openDetails}
              aria-label={t("VIEW_DETAILS", { name: label })}
              flexShrink={0}
              borderRadius="100"
              overflow="hidden"
            >
              <SmartImage
                image={image}
                width={THUMBNAIL_SIZE}
                height={THUMBNAIL_SIZE}
                shape="square"
              />
            </LinkButton>
          )}
          <Flex flexDir="column" gap="100" minW="0">
            <LinkButton type="button" onClick={openDetails}>
              {label}
            </LinkButton>
            <Flex gap="200" alignItems="center" flexWrap="wrap">
              <NumericStepper
                value={item.quantity}
                min={0}
                max={variant?.inventoryCount ?? item.quantity}
                onChange={setQuantity}
              />
              <LinkButton
                type="button"
                onClick={remove}
                color="text.error.mid"
              >
                <Text as="span" visual="smallMedium" color="inherit">
                  {t("REMOVE")}
                </Text>
              </LinkButton>
            </Flex>
          </Flex>
        </Flex>
      </Table.Header>
      <Table.Cell whiteSpace="nowrap" verticalAlign="top">
        ${formatPrice(item.total)}
      </Table.Cell>
    </Table.Row>
  );
};
